import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EnvironmentService } from './environment.service';
import { RequestHelper } from '@shared/helpers/request.helper';

export type CommentEntityType = 'course' | 'learner' | 'ticket' | 'complaint';

export interface GetCommentsOptions {
    entityType: CommentEntityType;
    entityId: string;
    includeRelated?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CommentApiService {
    constructor(
        private httpClient: HttpClient,
        private environmentService: EnvironmentService
    ) { }

    private get baseUrl(): string {
        return `${this.environmentService.getCurrentApiUrl()}/comments`;
    }

    /**
     * Get comments for a specific entity
     * @param options - Options for fetching comments
     * @param options.entityType - The type of entity (course, learner, ticket, complaint)
     * @param options.entityId - The ID of the entity
     * @param options.includeRelated - When true, includes related comments using denormalized fields
     *                                  For example, entityType=learner with includeRelated=true will get
     *                                  all comments from learner, tickets, and complaints related to that learner
     * @returns Observable of comments array
     */
    getComments(options: GetCommentsOptions): Observable<any> {
        const { entityType, entityId, includeRelated } = options;
        const params = includeRelated ? RequestHelper.createRequestOption({ includeRelated }) : {};
        return this.httpClient.get<any>(`${this.baseUrl}/${entityType}/${entityId}`, { params });
    }

    createComment(comment: any): Observable<any> {
        const { entityType, entityId, ...payload } = comment;
        return this.httpClient.post<any>(`${this.baseUrl}/${entityType}/${entityId}`, payload);
    }

    updateComment(comment: any): Observable<any> {
        return this.httpClient.put<any>(this.baseUrl, comment);
    }

    deleteComment(id: string): Observable<any> {
        return this.httpClient.delete<any>(`${this.baseUrl}/${id}`);
    }

    recallComment(id: string): Observable<any> {
        return this.httpClient.put<any>(`${this.baseUrl}/recall/${id}`, {});
    }
}

